import { useContext } from 'react'
import Icon from 'common/Icon'
import Card from 'react-bootstrap/Card'
import Accordion from 'react-bootstrap/Accordion'
import AccordionContext from 'react-bootstrap/AccordionContext'
import { LATTE } from 'constants/colors'
import styles from './index.module.scss'
import { providerSearchNumber } from 'constants/common'
import NumberFormat from 'react-number-format'
import { seFriendlyPhoneNumber } from 'app/SeFriendly/constants'
import PhoneNumberLink from 'common/PhoneNumberLink'
import useFooterLinkUrl from '../hooks/useFooterLinkUrl'

const CustomToggle = ({ children, eventKey }) => {
  const currentEventKey = useContext(AccordionContext)
  const isCurrentEventKey = currentEventKey === eventKey

  return (
    <div className="d-flex align-items-center justify-content-between">
      <span className="pr-3">{children}</span>
      <button
        aria-expanded={isCurrentEventKey}
        aria-label={
          isCurrentEventKey
            ? 'Toggle Up Arrow - Close'
            : 'Toggle Down Arrow - Open'
        }
        className="ml-3 float-right button_default"
      >
        <Icon
          icon={isCurrentEventKey ? 'toggleUpArrow' : 'toggleDownArrow'}
          fill={LATTE}
          className="fa-md"
        />
      </button>
    </div>
  )
}

type MobileAccordionLinksProps = {
  thriveworksLinks: FooterLink[]
  resourcesLinks: FooterLink[]
  servicesLinks: FooterLink[]
  insuranceLinks: FooterLink[]
  isSeFriendly?: boolean
}

const MobileAccordionLinks: React.FC<MobileAccordionLinksProps> = ({
  thriveworksLinks,
  resourcesLinks,
  servicesLinks,
  insuranceLinks,
  isSeFriendly = false,
}) => {
  const enhanceFooterUrl = useFooterLinkUrl()
  const { phoneNumber, formattedPhoneNumber } = providerSearchNumber
  const phoneNumberCTA = isSeFriendly
    ? seFriendlyPhoneNumber.formattedPhoneNumber
    : formattedPhoneNumber

  return (
    <>
      <Accordion>
        <Card
          key={0}
          className={`rounded-0 shadow-none ${styles.links_horizontal_line}`}
        >
          <Accordion.Toggle
            as={Card.Header}
            eventKey="0"
            className={`c-pointer ${styles.links_container}`}
          >
            <CustomToggle eventKey="0">
              <h3
                className={`mb-0 ${styles.links_header} ${styles.mobile_links_header}`}
              >
                Thriveworks
              </h3>
            </CustomToggle>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="0">
            <Card.Body className={`pt-1 ${styles.links_container}`}>
              <ul
                className={`m-0 p-0 list-unstyled ${styles.links_items_container}`}
              >
                {thriveworksLinks.map((data) => (
                  <li
                    className={`d-flex ${styles.links_items}`}
                    key={`${data.link + data.label}`}
                  >
                    <a target="_blank" rel="noreferrer" href={data.link}>
                      {data.label}
                    </a>
                    {data.label === 'Careers' && (
                      <div
                        className={`${styles.hiring_badge} rounded-pill ml-2 px-2 py-1 flex-center`}
                      >
                        Hiring
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </Card.Body>
          </Accordion.Collapse>
        </Card>

        <Card
          key={1}
          className={`rounded-0 shadow-none ${styles.links_horizontal_line}`}
        >
          <Accordion.Toggle
            as={Card.Header}
            eventKey="1"
            className={`c-pointer ${styles.links_container}`}
          >
            <CustomToggle eventKey="1">
              <h3
                className={`mb-0 ${styles.links_header} ${styles.mobile_links_header}`}
              >
                Reach us
              </h3>
            </CustomToggle>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="1">
            <Card.Body className={`pt-1 ${styles.links_container}`}>
              <ul
                className={`m-0 p-0 list-unstyled ${styles.links_items_container}`}
              >
                <li className={styles.links_items}>
                  <PhoneNumberLink
                    phoneNumberCTA={phoneNumberCTA}
                    isNextLink={true}
                    href={`tel:${
                      isSeFriendly
                        ? seFriendlyPhoneNumber.phoneNumber
                        : phoneNumber
                    }`}
                  >
                    <NumberFormat
                      value={phoneNumberCTA}
                      format="(###) ###-####"
                      displayType="text"
                    />
                  </PhoneNumberLink>
                </li>
                <li className={styles.links_items}>
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={enhanceFooterUrl('/contact-thriveworks/')}
                  >
                    Contact us
                  </a>
                </li>
              </ul>
            </Card.Body>
          </Accordion.Collapse>
        </Card>

        <Card
          key={2}
          className={`rounded-0 shadow-none ${styles.links_horizontal_line}`}
        >
          <Accordion.Toggle
            as={Card.Header}
            eventKey="2"
            className={`c-pointer ${styles.links_container}`}
          >
            <CustomToggle eventKey="2">
              <h3
                className={`mb-0 ${styles.links_header} ${styles.mobile_links_header}`}
              >
                Resources
              </h3>
            </CustomToggle>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="2">
            <Card.Body className={`pt-1 ${styles.links_container}`}>
              <ul
                className={`m-0 p-0 list-unstyled ${styles.links_items_container}`}
              >
                {resourcesLinks.map((data) => (
                  <li
                    className={styles.links_items}
                    key={`${data.link + data.label}`}
                  >
                    <a target="_blank" rel="noreferrer" href={data.link}>
                      {data.label}
                    </a>
                  </li>
                ))}
              </ul>
            </Card.Body>
          </Accordion.Collapse>
        </Card>

        <Card
          key={3}
          className={`rounded-0 shadow-none ${styles.links_horizontal_line}`}
        >
          <Accordion.Toggle
            as={Card.Header}
            eventKey="3"
            className={`c-pointer ${styles.links_container}`}
          >
            <CustomToggle eventKey="3">
              <h3
                className={`mb-0 ${styles.links_header} ${styles.mobile_links_header}`}
              >
                Services
              </h3>
            </CustomToggle>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="3">
            <Card.Body className={`pt-1 ${styles.links_container}`}>
              <ul
                className={`m-0 p-0 list-unstyled ${styles.links_items_container}`}
              >
                {servicesLinks.map((data) => (
                  <li
                    className={styles.links_items}
                    key={`${data.link + data.label}`}
                  >
                    <a target="_blank" rel="noreferrer" href={data.link}>
                      {data.label}
                    </a>
                  </li>
                ))}
              </ul>
            </Card.Body>
          </Accordion.Collapse>
        </Card>

        <Card
          key={4}
          className={`rounded-0 shadow-none ${styles.links_horizontal_line}`}
        >
          <Accordion.Toggle
            as={Card.Header}
            eventKey="4"
            className={`c-pointer ${styles.links_container}`}
          >
            <CustomToggle eventKey="4">
              <h3
                className={`mb-0 ${styles.links_header} ${styles.mobile_links_header}`}
              >
                Insurance we accept
              </h3>
            </CustomToggle>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="4">
            <Card.Body className={`pt-1 ${styles.links_container}`}>
              <ul
                className={`m-0 p-0 list-unstyled ${styles.links_items_container}`}
              >
                {insuranceLinks.map((data) => (
                  <li
                    className={styles.links_items}
                    key={`${data.link + data.label}`}
                  >
                    <a target="_blank" rel="noreferrer" href={data.link}>
                      {data.label}
                    </a>
                  </li>
                ))}
              </ul>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
    </>
  )
}

export default MobileAccordionLinks
